/**
 * reset-password.js
 */

import React from 'react';
import axios from 'axios';
import { 
  useMediaQuery,
  Box,
  Typography,
  Link as MuiLink,
  Button,
} from '@mui/material';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { AuthContext } from 'contexts/auth/auth-context';
import { useForm, FormProvider } from 'react-hook-form';
import PageTitle from 'components/page-title';
import FeatureBox from 'components/feature-box';
import ChangePasswordFields from 'components/change-password-fields';
import logoBadge from 'assets/images/logo-badge.png';


const STATE = {
  TOKEN_INVALID: 'token-invalid',
  TOKEN_VALID: 'token-valid',
  SUCCESS_MOBILE: 'success-mobile',
};


export default function ResetPassword(props) {
  const { isCreating } = props;
  const history = useHistory();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { userId, date, token } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setIsAuthenticated, setIsForgotPasswordOpen } = React.useContext(AuthContext);
  const [isLoading, setIsLoading] = React.useState(true);
  const [state, setState] = React.useState();
  const formContext = useForm({ defaultValues: { newPassword: '', confirmPassword: '' } });
  const { handleSubmit } = formContext;
  const title = isCreating ? 'Create Password' : 'Reset Password';
  

  React.useEffect(() => {
    document.title = `PACKTRACK - ${title}`;
    setIsForgotPasswordOpen(false);
  }, [title, setIsAuthenticated, setIsForgotPasswordOpen]);


  // validate token
  React.useEffect(() => {
    (async() => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `/account/validate-reset-password-token`, 
          { params: { userId, date, token, isCreating }, bypassErrorHandling: true });
        if (response.isSuccess) {
          setState(STATE.TOKEN_VALID);
        } else {
          setState(STATE.TOKEN_INVALID);
        }
      } catch (e) {
        setState(STATE.TOKEN_INVALID);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [userId, date, token, isCreating]);


  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `/account/reset-password`, 
        { userId, date, token, isCreating, ...data },
        { bypassErrorHandling: true });
      if (response.isSuccess) {
        if (!isMobile) {
          enqueueSnackbar(`Password ${isCreating ? 'creation' : 'reset'} success.`, {
            variant: 'success',
            persist: true,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          });
          history.push('/');
        } else {
          setState(STATE.SUCCESS_MOBILE);
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      enqueueSnackbar(`Unable to ${isCreating ? 'create' : 'reset'} your password.`, {
        variant: 'error',
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      width: '100vw',
      backgroundColor: 'hsl(204, 97%, 12%)'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'sys.surface',
        borderRadius: '4px',
        p: 2,
        m: 2,
        maxWidth: '100%',
        width: '400px',
      }}>
        <Box sx={{
          mt: '-75px',
          mb: 2,
          '& img': { height: '150px' }
        }}>
          <img src={logoBadge} alt='PACKTRACK logo' />
        </Box>

        {state !== STATE.SUCCESS_MOBILE &&
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <PageTitle iconName='password' title={title} />
        </Box>}

        <FeatureBox loading={isLoading} spinnerSize={50} spinnerTop={-20} sx={{ minHeight: 100 }}>

          {/* INVALID TOKEN */}
          {state === STATE.TOKEN_INVALID &&
          <Box>
            <Typography variant='subtitle1'>
              This password reset link is no longer valid. 
              If you would still like to reset your password, <></>
              <MuiLink 
                component={Link} 
                to={`/`} 
                onClick={() => {
                  setIsForgotPasswordOpen(true);
                }}>click here</MuiLink>.
            </Typography>
          </Box>}
          
          {/* VALID TOKEN */}
          {state === STATE.TOKEN_VALID &&
          <Box sx={{
            '& .MuiGrid-item': {
              my: 2
            },
            '& .MuiTextField-root': {
              width: '240px',
            }
          }}>
            <Typography variant='subtitle1'>Enter your new password:</Typography>
            <FormProvider {...formContext}>
              <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
                <ChangePasswordFields />
                <Button
                  variant='contained'
                  color='secondary'
                  sx={{ width: '175px', mt: 2, py: 1.3 }}
                  children='Submit'
                  onClick={() => handleSubmit(onSubmit)()}
                />
              </form>
            </FormProvider>
          </Box>}
          
          {/* SUCCESS MOBILE */}
          {state === STATE.SUCCESS_MOBILE &&
          <Box>
            <Typography variant='h5' sx={{ mb: 3 }}>Password reset sucessfully!</Typography>
            <Typography variant='subtitle1' sx={{ mb: 2, lineHeight: 1.5 }}>
              Open the PACKTRACK mobile app and login using your new password.
            </Typography>
            <Typography variant='caption'>
              You can close this browser window.
            </Typography>
          </Box>}
          
        </FeatureBox>
      </Box>
    </Box>
  )
}